import React, { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import ChartCard from './components/ChartCard'
import Section from './components/Section'
import {
  AXIS_TICK,
  CHART_COLORS,
  MOBILE_SCREEN_LABEL_KEYS,
  MODULE_SERIES,
  TOOLTIP_STYLE,
  formatPeriodStart,
  formatWeekday,
  hasNonZeroValue,
} from './constants'
import { OPERATION_PRODUCT_ANALYTICS_USAGE } from './operations-gql/queries'
import { ProductAnalyticsPeriod, ProductAnalyticsPlatform, UsageQuery, UsageVariables } from './types'

type UsageSectionProps = {
  period: ProductAnalyticsPeriod
  platform: ProductAnalyticsPlatform
}

const MODULE_LABEL_KEYS: Record<typeof MODULE_SERIES[number]['dataKey'], string> = {
  codes: 'codes',
  users: 'users',
  privateWifi: 'private_wifi',
  eventWifi: 'event_wifi',
  accessPointZones: 'access_point_zones',
  wan: 'wan',
  bypass: 'bypass',
  streamCast: 'stream_cast',
}

const TRAFFIC_CHART_MIN_HEIGHT = 224
const TRAFFIC_ROW_HEIGHT = 20
const TRAFFIC_AXIS_HEIGHT = 32

const UsageSection: React.FC<UsageSectionProps> = ({ period, platform }) => {
  const { t, i18n } = useTranslation()
  const isMobile = platform === 'MOBILE'
  const { data, loading, error } = useQuery<UsageQuery, UsageVariables>(
    OPERATION_PRODUCT_ANALYTICS_USAGE,
    { variables: { period, platform, withActionsByModule: !isMobile }, errorPolicy: 'all' }
  )
  const usage = data?.productAnalytics
  const pageViewsLabel = t(`stats.product_analytics.usage.${isMobile ? 'screen_views' : 'page_views'}`)
  const trafficTranslationKey = isMobile ? 'traffic_by_screen' : 'traffic_by_section'

  const traffic = useMemo(() => {
    const trafficBySection = usage?.trafficBySection ?? []
    if (!isMobile) return trafficBySection

    return trafficBySection.map(({ label, value }) => {
      const screenLabelKey = MOBILE_SCREEN_LABEL_KEYS[label]
      return { label: screenLabelKey ? t(`stats.product_analytics.usage.screens.${screenLabelKey}`) : label, value }
    })
  }, [usage?.trafficBySection, isMobile, t])

  const weekdays = useMemo(() => (
    usage?.usageByWeekday?.map(({ label, value }) => ({ label: formatWeekday(label, i18n.language), value })) ?? []
  ), [usage?.usageByWeekday, i18n.language])

  const hours = useMemo(() => (
    usage?.usageByHour?.map(({ label, value }) => ({ label: `${label}h`, value })) ?? []
  ), [usage?.usageByHour])

  const trafficChartHeight = Math.max(
    TRAFFIC_CHART_MIN_HEIGHT,
    (usage?.trafficBySection?.length ?? 0) * TRAFFIC_ROW_HEIGHT + TRAFFIC_AXIS_HEIGHT
  )

  const hasFieldError = (field: unknown[] | null | undefined) => Boolean(error) && !field

  return (
    <Section id="product-analytics-usage" title={t('stats.product_analytics.usage.title')} columns={{ xs: 1, md: 2 }}>
      <ChartCard
        title={t(`stats.product_analytics.usage.${trafficTranslationKey}`)}
        description={t(`stats.product_analytics.usage.${trafficTranslationKey}_description`)}
        loading={loading}
        hasError={hasFieldError(usage?.trafficBySection)}
        isEmpty={!hasNonZeroValue(usage?.trafficBySection, ['value'])}
        bodyHeight="auto"
      >
        <ResponsiveContainer width="100%" height={trafficChartHeight}>
          <BarChart data={traffic} layout="vertical" margin={{ left: 24 }}>
            <CartesianGrid horizontal={false} stroke="#F3F4F6" />
            <XAxis type="number" allowDecimals={false} tick={AXIS_TICK} tickLine={false} />
            <YAxis type="category" dataKey="label" width={120} interval={0} tick={AXIS_TICK} tickLine={false} />
            <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: '#F9FAFB' }} />
            <Bar dataKey="value" name={pageViewsLabel} fill={CHART_COLORS.primary} radius={[0, 6, 6, 0]} barSize={10} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {!isMobile && (
        <ChartCard
          title={t('stats.product_analytics.usage.actions_by_module')}
          description={t('stats.product_analytics.usage.actions_by_module_description')}
          loading={loading}
          hasError={hasFieldError(usage?.actionsByModule)}
          isEmpty={!hasNonZeroValue(usage?.actionsByModule, MODULE_SERIES.map(({ dataKey }) => dataKey))}
          bodyHeight={224}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={usage?.actionsByModule ?? []}>
              <CartesianGrid vertical={false} stroke="#F3F4F6" />
              <XAxis
                dataKey="periodStart"
                tickFormatter={(value: string) => formatPeriodStart(value, i18n.language)}
                tick={AXIS_TICK}
                tickLine={false}
              />
              <YAxis allowDecimals={false} tick={AXIS_TICK} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                cursor={{ fill: '#F9FAFB' }}
                labelFormatter={(value: string) => formatPeriodStart(value, i18n.language)}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {MODULE_SERIES.map(({ dataKey, color }) => (
                <Bar
                  key={dataKey}
                  dataKey={dataKey}
                  name={t(`stats.product_analytics.usage.modules.${MODULE_LABEL_KEYS[dataKey]}`)}
                  stackId="modules"
                  fill={color}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      <ChartCard
        title={t('stats.product_analytics.usage.usage_by_weekday')}
        loading={loading}
        hasError={hasFieldError(usage?.usageByWeekday)}
        isEmpty={!hasNonZeroValue(usage?.usageByWeekday, ['value'])}
        bodyHeight={160}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={weekdays}>
            <CartesianGrid vertical={false} stroke="#F3F4F6" />
            <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} />
            <YAxis allowDecimals={false} tick={AXIS_TICK} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: '#F9FAFB' }} />
            <Bar dataKey="value" name={pageViewsLabel} fill={CHART_COLORS.quaternary} radius={[6, 6, 0, 0]} barSize={24} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard
        title={t('stats.product_analytics.usage.usage_by_hour')}
        loading={loading}
        hasError={hasFieldError(usage?.usageByHour)}
        isEmpty={!hasNonZeroValue(usage?.usageByHour, ['value'])}
        bodyHeight={160}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={hours}>
            <CartesianGrid vertical={false} stroke="#F3F4F6" />
            <XAxis dataKey="label" interval={1} tick={AXIS_TICK} tickLine={false} />
            <YAxis allowDecimals={false} tick={AXIS_TICK} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: '#F9FAFB' }} />
            <Bar dataKey="value" name={pageViewsLabel} fill={CHART_COLORS.secondary} radius={[6, 6, 0, 0]} barSize={12} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </Section>
  )
}

export default UsageSection
